/* ─────────────────────────────────────────────────────────────────────────
   MLMT quick-add — the card "Add to Cart" button and its cart helpers
   ─────────────────────────────────────────────────────────────────────────
   Lifted out of assets/mlmt-collection.js on 11 Sep 2026.

   WHY: every product card carries onclick="addToCart(this)" on a full-width,
   always-visible button — but addToCart() was defined only in the collection
   page's script. The same card renders on the home page (mlmt-bestsellers),
   on every PDP ("You may also like" via product-recommendations) and in
   featured-collection sections, none of which load that script. On those
   grids the button was a ReferenceError: a black ADD TO CART that did nothing
   when pressed. Verified by the audit on the deployed theme.

   This file is loaded ONCE from layout/theme.liquid with defer, ahead of
   mlmt-collection.js in document order, so the globals exist everywhere the
   card can appear and before the collection script's own references to them.

   Functions here are top-level declarations — global, exactly as they were
   when inline — because the card's inline onclick resolves them by name.
   ───────────────────────────────────────────────────────────────────────── */

function addToCart(button) {
  const variantId = button.getAttribute('data-variant-id');
  const productTitle = button.getAttribute('data-product-title');
  const isAvailable = button.getAttribute('data-available') === 'true';
  
  if (!isAvailable) {
    return;
  }
  
  const originalText = button.textContent;
  
  button.textContent = 'Adding...';
  button.classList.add('loading');
  button.disabled = true;
  
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id: variantId,
      quantity: 1
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  })
  .then(data => {
    button.textContent = 'Added!';
    button.classList.remove('loading');
    button.classList.add('success');
    
    showCartNotification('Item added to cart!');
    refreshCartAndOpenDrawer();
    
    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove('success');
      button.disabled = false;
    }, 2000);
  })
  .catch(error => {
    button.textContent = 'Error';
    button.classList.remove('loading');
    button.style.background = 'var(--mlmtSale)';
    
    showCartNotification('Error adding item to cart. Please try again.', 'error');
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
      button.disabled = false;
    }, 2000);
  });
}

function refreshCartAndOpenDrawer() {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cartData => {
      updateCartCount(cartData.item_count);
      openCartDrawer();
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: cartData 
      }));
    })
    .catch(error => {
      // Silent fail
    });
}

function updateCartCount(count) {
  const cartCountSelectors = [
    '.cart-count',
    '.cart-item-count',
    '[data-cart-count]',
    '#cart-count',
    '.header-cart-count',
    '.cart-bubble',
    '.cart-count-bubble',
    '.header__icon--cart .visually-hidden'
  ];
  
  cartCountSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
      if (element) {
        element.textContent = count;
        element.style.display = count > 0 ? 'block' : 'none';
      }
    });
  });
}

function openCartDrawer() {
  /* 11 Sep 2026: this used to fire 'ajaxProduct:added' (opens), then .click()
     every header cart link (the synthetic click bubbled to window and CLOSED
     the drawer the theme had just opened), then 'cart:open' (opens again) —
     an open/close/open in one tick: overlay fade-out-and-in, focus trap
     toggled twice, 'cart:updated' fired twice. theme.CartDrawer listens for
     'cart:open' and theme.CartForm for 'cart:build'; one of each is enough.
     The header click is a fallback only, for a template that opened without
     a CartDrawer instance. */
  if (window.mlmtCartTabs && typeof window.mlmtCartTabs.select === 'function') {
    window.mlmtCartTabs.select('cart');
  }
  document.dispatchEvent(new CustomEvent('cart:build'));
  document.dispatchEvent(new CustomEvent('cart:open'));

  setTimeout(function () {
    if (document.documentElement.classList.contains('js-drawer-open')) return;
    const trigger = document.querySelector('.js-drawer-open-cart');
    if (trigger) {
      try { trigger.click(); } catch (error) { /* nothing to fall back to */ }
    }
  }, 300);
}

function showCartNotification(message, type = 'success') {
  const existingNotif = document.querySelector('.cart-notification');
  if (existingNotif) {
    existingNotif.remove();
  }
  
  const notification = document.createElement('div');
  notification.className = 'cart-notification';
  notification.textContent = message;
  
  const bgColor = type === 'error' ? 'var(--mlmtSale)' : '#10b981';
  
  notification.style.cssText = `
    position: fixed;
    top: 80px;
    right: 20px;
    background: ${bgColor};
    color: var(--mlmtInk);
    padding: 12px 20px;
    border-radius: 8px;
    font-family:var(--fontBody);
    font-size: 14px;
    font-weight: 500;
    z-index: 10000;
    box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
    animation: slideInNotification 0.3s ease-out;
    cursor: pointer;
    max-width: 300px;
    word-wrap: break-word;
  `;
  
  if (!document.querySelector('#cart-notification-styles')) {
    const style = document.createElement('style');
    style.id = 'cart-notification-styles';
    style.textContent = `
      @keyframes slideInNotification {
        from { opacity: 0; transform: translateX(100%); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideOutNotification {
        from { opacity: 1; transform: translateX(0); }
        to { opacity: 0; transform: translateX(100%); }
      }
      .cart-notification.hiding {
        animation: slideOutNotification 0.3s ease-out forwards;
      }
    `;
    document.head.appendChild(style);
  }
  
  document.body.appendChild(notification);
  
  setTimeout(() => hideNotification(), 4000);
  notification.addEventListener('click', hideNotification);
  
  function hideNotification() {
    if (notification && notification.parentNode) {
      notification.classList.add('hiding');
      setTimeout(() => notification.remove(), 300);
    }
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   THE FREE-GIFT RULE, AND THE ONE PIECE COUNT — 12 Sep 2026
   ─────────────────────────────────────────────────────────────────────────
   The free-gift app grants two gifts by piece count. The theme showed that
   ladder in three places and counted it three different ways — the drawer
   bar took every non-gift line, the PDP tiles took cart.item_count (gifts
   included) plus a number scraped from a label, and none of them was the
   app's rule, which is: the quantity of lines whose product is in ONE
   collection ("Most Popular"), at 3 and at 5. A gift card lit the 3-piece
   tick; a granted gift pushed the tiles to "5 pieces — unlocked" at four.

   Two functions, here because this file is on every page (layout/theme,
   defer) ahead of everything that needs them:

   mlmtGiftRule()  reads the campaign out of the app's own config, the JSON
                   it ships in the page as #kite-app-data — the thresholds,
                   the qualifying collection handles, the heading. Nothing is
                   typed into the theme, so a campaign edit in the app is
                   picked up with no deploy. The app renders that JSON at the
                   end of <body>, so this is looked up lazily and only a
                   successful read is cached.

   mlmtGiftCount() the app's number: quantity of the cart's lines whose
                   product is in a qualifying collection, gift lines skipped.
                   It reads the rendered line items — snippets/cart-item marks
                   every row with data-qty, data-collections and data-gift —
                   rather than /cart.js, which does not carry collections and
                   which three app embeds already hammer (see mlmt-cart-
                   throttle). Rows are what theme.CartForm has just painted, so
                   callers recount on 'cart:built'. If the app's config cannot
                   be read the count falls back to every non-gift line, which
                   is what it used to be.
   ───────────────────────────────────────────────────────────────────────── */
function mlmtGiftRule() {
  if (mlmtGiftRule._rule) return mlmtGiftRule._rule;
  var el = document.getElementById('kite-app-data');
  if (!el) return null;
  var data;
  try { data = JSON.parse(el.textContent || ''); } catch (e) { return null; }

  function strip(s) {
    return String(s || '')
      .replace(/[\u2190-\u2BFF\uFE0F\u200D]|[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  function text(node) {
    /* the app keys its copy by language; take the page's, else English, else the first */
    if (!node || typeof node !== 'object') return '';
    var lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    var pick = node[lang] || node.en || node[Object.keys(node)[0]];
    return (pick && pick.conditionNotMetText) || '';
  }

  var campaigns = (data && data.consolidatedCombinedDiscount) || [];
  for (var c = 0; c < campaigns.length; c++) {
    var camp = campaigns[c];
    if (!camp || camp.isDeleted || camp.isEnabled === false) continue;
    var segs = (camp.segmentsData && camp.segmentsData.segmentsList) || [];
    var handles = {}, seen = {}, marks = [];
    segs.forEach(function (seg) {
      if (!seg || (seg.goalType && seg.goalType !== 'FREE_GIFT')) return;
      var rules = (seg.buyXRulesData && seg.buyXRulesData.rulesList) || [];
      rules.forEach(function (r) {
        if (!r || r.ruleType !== 'collections') return;
        var rv = r.ruleValue || {};
        if (rv.qualifierType && rv.qualifierType !== 'overallQty') return;
        var at = parseInt(rv.qualifierValue, 10);
        (rv.value || []).forEach(function (v) { if (v && v.handle) handles[v.handle] = 1; });
        /* a "threshold" in the dozens is a misparse, not a generous campaign */
        if (at >= 1 && at <= 20 && !seen[at]) { seen[at] = 1; marks.push({ at: at, label: '' }); }
      });
    });
    if (!marks.length) continue;
    marks.sort(function (a, b) { return a.at - b.at; });
    var heading = strip(text(camp.widgetSettings && camp.widgetSettings.heading && camp.widgetSettings.heading.title));
    mlmtGiftRule._rule = {
      handles: Object.keys(handles),
      marks: marks,
      heading: heading,
      /* "Free Mystery Tennis Jewelry at 3 & 5 Pieces" — the part before " at " is the gift's name */
      name: heading.replace(/\s+at\s+.*$/i, '').trim() || heading
    };
    return mlmtGiftRule._rule;
  }
  return null;
}

function mlmtGiftCount() {
  var rule = mlmtGiftRule();
  var handles = (rule && rule.handles) || [];
  var rows = document.querySelectorAll('.cart__item[data-key]');
  var seen = {}, n = 0;
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i];
    var key = row.getAttribute('data-key');
    /* the cart page and the drawer can both hold the same line — count it once */
    if (seen[key]) continue;
    seen[key] = 1;
    if (row.hasAttribute('data-gift')) continue;
    var qty = parseInt(row.getAttribute('data-qty'), 10);
    if (!(qty > 0)) continue;
    if (handles.length) {
      var mine = (row.getAttribute('data-collections') || '').split(',');
      var hit = false;
      for (var h = 0; h < handles.length; h++) { if (mine.indexOf(handles[h]) !== -1) { hit = true; break; } }
      if (!hit) continue;
    }
    n += qty;
  }
  return n;
}
